import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { SORT_NEWEST } from '../../config/configSearch';
import { fetchPageAssets } from '../../ducks/hostedAssets.duck';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { storableError } from '../../util/errors';
import { getBanners } from '../../util/api';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { parse } from '../../util/urlHelpers';
import { PARTNER_USER_FIELDS, isVerifiedBusiness } from '../../util/userTypes';
import * as log from '../../util/log';

import { USE_HOSTED_LANDING_PAGE } from './LandingPage.config';
import { STRIPS, buildStripCategoryParams, stripParamKey } from './landingStrips';
import { hasEnoughPrivate, spreadPartnerListings } from './spreadPartnerListings';

export const ASSET_NAME = 'landing-page';

// Re-export for back-compat with any importers using these names.
export { SECTION_RECENTLY_LISTED } from './landingStrips';

const PER_PAGE = 12;

// The carousels read more than they show, so the slot rule has private items
// to place between partner items (spreadPartnerListings.js). Further pages are
// read only when these hold too few private items, e.g. right after a partner
// published a large batch.
const FETCH_PER_PAGE = 48;
const MAX_FETCH_PAGES = 3;

/** Is this listing's seller a verified Zuprize Partner? Reads the included author. */
const partnerCheckFor = included => {
  const users = new Map((included || []).filter(e => e.type === 'user').map(u => [u.id.uuid, u]));
  return listing => {
    const authorId = listing.relationships?.author?.data?.id?.uuid;
    return isVerifiedBusiness(users.get(authorId));
  };
};

// ================ Banners ================ //

/**
 * Homepage banners, managed by an admin on BannerAdminPage. Read through our
 * own server because they are stored as draft listings the public API cannot
 * see (server/api/banners.js explains why).
 */
export const fetchBannersThunk = createAsyncThunk(
  'LandingPage/fetchBanners',
  ({ marketplaceRootURL }) => {
    return getBanners(marketplaceRootURL).then(response => response.banners || []);
  }
);

// ================ Async Thunks ================ //

/**
 * Fetch one landing page strip's listings (e.g. "Recently listed").
 * Mirrors the SearchPage / featuredListings query shape so the resulting
 * entities are usable by the existing ListingCard primitives.
 *
 * @param {Object} arg
 * @param {string} arg.sectionId – stable id used to key the strip in state
 * @param {Object} [arg.categoryParams] – e.g. { pub_categoryLevel1: 'topper' }
 * @param {string} [arg.sort] – defaults to SORT_NEWEST (newest first, the
 *   SearchPage "Nyeste" sort option in configSearch.js)
 * @param {Object} arg.listingImageConfig – { aspectWidth, aspectHeight, variantPrefix }
 */
const fetchLandingPageStripPayloadCreator = async (arg, thunkAPI) => {
  const { extra: sdk, dispatch, rejectWithValue } = thunkAPI;
  const { sectionId, categoryParams = {}, sort = SORT_NEWEST, listingImageConfig } = arg;

  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } =
    listingImageConfig || {};
  const aspectRatio = aspectHeight / aspectWidth;

  const queryPage = page =>
    sdk.listings.query({
      perPage: FETCH_PER_PAGE,
      page,
      sort,
      minStock: 1,
      stockMode: 'match-undefined',
      include: ['images', 'author', 'author.profileImage'],
      ...categoryParams,
      // Listing payload kept tight: only the fields the ListingCard renders.
      // The publicData keys mirror the listing field config in
      // src/config/configListing.js — `merke` (brand), `tilstand`
      // (condition), and the two size variants (`storrelse-klar` for
      // clothing, `storrelse-sko` for shoes; the card picks whichever the
      // listing has).
      'fields.listing': [
        'title',
        'price',
        'deleted',
        'state',
        'publicData.listingType',
        'publicData.transactionProcessAlias',
        'publicData.unitType',
        'publicData.cardStyle',
        'publicData.merke',
        'publicData.merke-annet',
        'publicData.tilstand',
        'publicData.storrelse-klar',
        'publicData.storrelse-sko',
      ],
      'fields.user': ['profile.displayName', 'profile.abbreviatedName', ...PARTNER_USER_FIELDS],
      'fields.image': [
        'variants.listing-card',
        'variants.listing-card-2x',
        'variants.scaled-small',
        // Author avatar variants — see ListingCard <Avatar />.
        'variants.square-xsmall',
        'variants.square-xsmall2x',
        'variants.square-small',
        'variants.square-small2x',
      ],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      'limit.images': 1,
    });

  const fetchAndSpread = async () => {
    const data = [];
    const included = [];
    let page = 1;
    let totalPages = 1;
    do {
      const response = await queryPage(page);
      data.push(...(response.data.data || []));
      included.push(...(response.data.included || []));
      totalPages = response.data.meta?.totalPages || 1;
      page += 1;
    } while (
      page <= totalPages &&
      page <= MAX_FETCH_PAGES &&
      !hasEnoughPrivate(data, partnerCheckFor(included), PER_PAGE)
    );
    const chosen = spreadPartnerListings(data, partnerCheckFor(included), PER_PAGE);
    return { data: { data: chosen, included } };
  };

  return fetchAndSpread()
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      return { sectionId, apiResponse: response };
    })
    .catch(error => {
      log.error(error, 'landing-page-strip-fetch-failed', { sectionId });
      return rejectWithValue({ sectionId, error: storableError(error) });
    });
};

export const fetchLandingPageStrip = createAsyncThunk(
  'LandingPage/fetchLandingPageStrip',
  fetchLandingPageStripPayloadCreator
);

// ================ Slice ================ //

const isPublishedAndNotDeleted = listing =>
  !listing.attributes.deleted && listing.attributes.state === 'published';

const initialState = {
  // Active banners for the top of the page, in admin-defined order.
  banners: [],
  // Per-strip state keyed by sectionId.
  // Shape: { [sectionId]: { listingIds: [], inProgress: false, error: null } }
  strips: {},
};

const landingPageSlice = createSlice({
  name: 'LandingPage',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchBannersThunk.fulfilled, (state, action) => {
        state.banners = action.payload;
      })
      .addCase(fetchBannersThunk.rejected, state => {
        // The section simply stays hidden; never break the page over a banner.
        state.banners = [];
      })
      .addCase(fetchLandingPageStrip.pending, (state, action) => {
        const { sectionId } = action.meta.arg;
        state.strips[sectionId] = {
          listingIds: state.strips[sectionId]?.listingIds || [],
          inProgress: true,
          error: null,
        };
      })
      .addCase(fetchLandingPageStrip.fulfilled, (state, action) => {
        const { sectionId, apiResponse } = action.payload;
        state.strips[sectionId] = {
          listingIds: apiResponse.data.data.filter(isPublishedAndNotDeleted).map(l => l.id),
          inProgress: false,
          error: null,
        };
      })
      .addCase(fetchLandingPageStrip.rejected, (state, action) => {
        const sectionId = action.payload?.sectionId;
        if (!sectionId) return;
        state.strips[sectionId] = {
          listingIds: state.strips[sectionId]?.listingIds || [],
          inProgress: false,
          error: action.payload.error,
        };
      });
  },
});

export default landingPageSlice.reducer;

// ================ Load data ================ //

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  // The hosted landing page asset is only needed when PageBuilder is the active
  // render path. In custom mode it would be wasted bandwidth on every page load.
  if (USE_HOSTED_LANDING_PAGE) {
    const pageAsset = { landingPage: `content/pages/${ASSET_NAME}.json` };
    return dispatch(fetchPageAssets(pageAsset, true));
  }

  const listingImageConfig = config?.layout?.listingImage;
  const categories = config?.categoryConfiguration?.categories || [];
  const queryParams = parse(search);

  // Strips fetched in parallel. Each one reads its chip selection from the
  // URL (`?strip-<sectionId>=<chipKey>`) so deep-linked / refreshed pages
  // hydrate with the right filters already applied.
  return Promise.all([
    dispatch(fetchBannersThunk({ marketplaceRootURL: config?.marketplaceRootURL })),
    ...STRIPS.map(strip => {
      const chipKey = queryParams[stripParamKey(strip.sectionId)] || null;
      return dispatch(
        fetchLandingPageStrip({
          sectionId: strip.sectionId,
          categoryParams: buildStripCategoryParams(strip.parentCategoryPath, chipKey, {
            excludeCategoryIds: strip.excludeCategoryIds,
            categories,
          }),
          listingImageConfig,
        })
      );
    }),
  ]);
};
